import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { DashboardScheduleWeekTaskCategoryChip } from "./DashboardScheduleWeekTaskCategoryChip";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";

import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import { cn } from "@/lib/utils";
import {
  DashboardInterview,
  DashboardScheduleTaskCategory,
  DashboardScheduleWeekDay,
  DashboardTask,
} from "@/types";

const COLLAPSED_ROW_HEIGHT_PX = 44;

export const DashboardScheduleWeekTasksBand = ({
  days,
  tasks,
  interviews,
  onOpenTasksView,
}: {
  days: DashboardScheduleWeekDay[];
  tasks: DashboardTask[];
  interviews: DashboardInterview[];
  onOpenTasksView: (day: Date) => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  const { byDay, total } = useMemo(
    () => dashboardScheduleWeek.buildWeekTaskCounts(tasks, interviews, days),
    [tasks, interviews, days],
  );

  /* Handlers */
  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  /* Render */
  const render = () => (
    <Row
      className="shrink-0 items-stretch border-b border-slate-200 bg-white"
      style={expanded ? undefined : { height: COLLAPSED_ROW_HEIGHT_PX }}
    >
      <TasksBandToggleColumn
        expanded={expanded}
        total={total}
        onToggle={handleToggle}
      />
      <div
        className="grid min-w-0 flex-1"
        style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
      >
        {days.map((day) => {
          const dayCounts = byDay.get(day.key);
          return (
            <TasksBandDayColumn
              key={day.key}
              expanded={expanded}
              categories={dayCounts?.categories ?? []}
              total={dayCounts?.total ?? 0}
              onClick={() => onOpenTasksView(day.date)}
            />
          );
        })}
      </div>
    </Row>
  );

  return render();
};

// Leftmost sticky column: the collapse toggle, plus the week's all-tasks total
// when collapsed (a "Tasks" label when expanded).
const TasksBandToggleColumn = ({
  expanded,
  total,
  onToggle,
}: {
  expanded: boolean;
  total: number;
  onToggle: () => void;
}) => (
  <Column
    className={cn(
      "shrink-0 items-center gap-0.5 border-r border-slate-100 bg-white",
      expanded ? "justify-start py-2" : "justify-center py-1",
    )}
    style={{ width: dashboardScheduleWeek.TIME_GUTTER_WIDTH_PX }}
  >
    <Button
      type="button"
      variant="ghost"
      onClick={onToggle}
      className="h-5 w-5 shrink-0 p-0 text-slate-400 hover:text-slate-600"
    >
      {expanded ? (
        <ChevronUpIcon className="h-4 w-4" />
      ) : (
        <ChevronDownIcon className="h-4 w-4" />
      )}
    </Button>
    {expanded ? (
      <Text className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
        Tasks
      </Text>
    ) : total > 0 ? (
      <TaskCountBadge count={total} />
    ) : null}
  </Column>
);

// One day column: category chips when expanded, the day's all-tasks count when
// collapsed. Clicking anywhere in the column opens the task list on that day.
const TasksBandDayColumn = ({
  expanded,
  categories,
  total,
  onClick,
}: {
  expanded: boolean;
  categories: { category: DashboardScheduleTaskCategory; count: number }[];
  total: number;
  onClick: () => void;
}) => {
  if (!expanded) {
    return (
      <Column
        onClick={onClick}
        className="min-w-0 cursor-pointer items-center justify-center border-l border-slate-100 hover:bg-slate-50/80"
      >
        {total === 0 ? <EmptyDash /> : <TaskCountBadge count={total} />}
      </Column>
    );
  }
  return (
    <Column
      onClick={onClick}
      className="min-w-0 cursor-pointer flex-wrap content-start gap-1 border-l border-slate-100 px-1.5 py-2 hover:bg-slate-50/80"
    >
      {categories.length === 0 ? (
        <EmptyDash className="w-full text-center" />
      ) : (
        categories.map((batch) => (
          <DashboardScheduleWeekTaskCategoryChip
            key={batch.category}
            category={batch.category}
            count={batch.count}
          />
        ))
      )}
    </Column>
  );
};

// Neutral (transparent-black) pill — the all-tasks count, spanning every category.
const TaskCountBadge = ({ count }: { count: number }) => (
  <span className="inline-flex min-w-[18px] items-center justify-center rounded-full border border-black/15 bg-black/5 px-1.5 text-[10px] font-semibold tabular-nums text-slate-600">
    {count}
  </span>
);

const EmptyDash = ({ className }: { className?: string }) => (
  <Text className={cn("text-[10px] italic text-slate-300", className)}>—</Text>
);
